import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Check, 
  X, 
  Zap, 
  Building2, 
  Rocket, 
  Crown,
  Users,
  Target,
  Search,
  Shield,
  Brain,
  Sparkles,
  ArrowRight,
  ChevronLeft,
  Briefcase,
  Radar,
  Loader2,
  AlertCircle,
  ArrowRightCircle
} from 'lucide-react';
import TrialRequestModal from '../components/modals/TrialRequestModal';
import { useAuth } from '../contexts/AuthContext';
import { getDashboardPathForUser } from '../lib/auth-routing';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Feature copy + icon mapping per plan id. Pricing comes from the
// backend catalog at runtime — DO NOT hardcode prices here. The
// `included` flag matches the launch-day feature set; tiers below
// reflect the user's "Sentinel removed from the base plan" decision.
const PLAN_VISUALS = {
  // Contractor
  SMALL_BUSINESS: {
    icon: Building2,
    color: 'slate',
    features: [
      { text: '2 Active Pursuits', included: true },
      { text: '3 User Seats', included: true },
      { text: 'Opportunity Intake', included: true },
      { text: 'RFP Shredder (Document Ingestion)', included: true },
      { text: 'Go/No-Go Dashboard', included: true },
      { text: 'SAM.gov Sentinel', included: false, locked: true },
      { text: 'Full Gap Analysis', included: false, locked: true },
      { text: 'Proposal Workspace', included: false, locked: true },
    ],
  },
  GROWTH: {
    icon: Rocket,
    color: 'teal',
    features: [
      { text: '10 Active Pursuits', included: true },
      { text: '10 User Seats', included: true },
      { text: 'SAM.gov Sentinel (Full)', included: true },
      { text: 'RFP Shredder + Bulk Mode', included: true },
      { text: 'Full Gap Analysis', included: true },
      { text: 'AI-Powered Teaming Suggestions', included: true },
      { text: 'Proposal Workspace', included: true },
      { text: 'Priority Support', included: true },
    ],
  },
  ENTERPRISE: {
    icon: Crown,
    color: 'purple',
    features: [
      { text: 'Unlimited Pursuits & Seats', included: true },
      { text: 'Multi-Division Support', included: true },
      { text: 'SAM.gov Sentinel (Unlimited)', included: true },
      { text: 'RFP Shredder + Bulk Mode (Unlimited)', included: true },
      { text: 'Priority Swarm Processing', included: true },
      { text: 'Dedicated Account Manager', included: true },
      { text: 'Custom Integrations', included: true },
      { text: 'SLA Guarantees', included: true },
    ],
  },
  // Consultant
  INDEPENDENT: {
    icon: Briefcase,
    color: 'slate',
    features: [
      { text: '2 Client Slots', included: true },
      { text: 'Unified Client Dashboard', included: true },
      { text: 'Opportunity Intake', included: true },
      { text: 'RFP Shredder (Document Ingestion)', included: true },
      { text: 'Go/No-Go Dashboard', included: true },
      { text: 'SAM.gov Sentinel', included: false, locked: true },
      { text: 'Associate Seats', included: false, locked: true },
      { text: 'Agency Branding', included: false, locked: true },
    ],
  },
  AGENCY: {
    icon: Users,
    color: 'purple',
    features: [
      { text: '5 Client Slots', included: true },
      { text: '3 Associate Seats', included: true },
      { text: 'SAM.gov Sentinel (Full)', included: true },
      { text: 'RFP Shredder + Bulk Mode', included: true },
      { text: 'Associate Deployment to Pursuits', included: true },
      { text: 'Full Gap Analysis', included: true },
      { text: 'Priority Support', included: true },
    ],
  },
  CONSULTANCY_ENTERPRISE: {
    icon: Crown,
    color: 'teal',
    features: [
      { text: 'Unlimited Client Slots', included: true },
      { text: 'Unlimited Associate Seats', included: true },
      { text: 'SAM.gov Sentinel (Unlimited)', included: true },
      { text: 'RFP Shredder + Bulk Mode (Unlimited)', included: true },
      { text: 'Dedicated Account Manager', included: true },
      { text: 'Custom Integrations', included: true },
      { text: 'SLA Guarantees', included: true },
    ],
  },
};

// Feature comparison table rows. The lookup function (`tierValue`)
// derives each cell from live plan data so the table never goes out of
// sync with the cards above. Set `gateBy` to a feature_id when a row
// should display "Coming Soon" until the platform Feature Release
// Console flips it on.
const COMPARISON_ROWS = [
  {
    label: 'SAM.gov Sentinel',
    icon: Radar,
    gateBy: 'sentinel',
    tierValue: (plan) => (plan.is_custom ? 'Unlimited' : (plan.feature_set || []).includes('sentinel') ? 'Full' : false),
  },
  {
    label: 'RFP Shredder',
    icon: Search,
    gateBy: 'document_ingestion',
    tierValue: (plan) => (plan.is_custom ? 'Unlimited' : (plan.feature_set || []).includes('document_ingestion') ? 'Included' : false),
  },
  {
    label: 'Opportunity Intake',
    icon: Target,
    gateBy: 'opportunity_intake',
    tierValue: (plan) => (plan.is_custom ? 'Unlimited' : (plan.feature_set || []).includes('opportunity_intake') ? 'Included' : false),
  },
  {
    label: 'Go/No-Go Dashboard',
    icon: Target,
    gateBy: 'go_no_go',
    tierValue: (plan) => (plan.is_custom ? 'Unlimited' : (plan.feature_set || []).includes('go_no_go') ? 'Included' : false),
  },
  {
    label: 'Active Pursuits',
    icon: Target,
    tierValue: (plan) => plan.is_custom ? 'Unlimited' : String(plan.limits?.max_pursuits ?? '—'),
  },
  {
    label: 'User Seats',
    icon: Users,
    tierValue: (plan) => plan.is_custom ? 'Unlimited' : String(plan.limits?.max_users ?? '—'),
  },
  {
    label: 'Gap Analysis',
    icon: Shield,
    gateBy: 'gap_analysis',
    tierValue: (plan) => plan.is_custom ? true : (plan.feature_set || []).includes('gap_analysis'),
  },
  {
    label: 'Bid Pursuit Scorecard',
    icon: Target,
    gateBy: 'bid_scorecard',
    tierValue: (plan) => plan.is_custom ? true : (plan.feature_set || []).includes('bid_scorecard'),
  },
  {
    label: 'Capability Matrix',
    icon: Brain,
    gateBy: 'capability_matrix',
    tierValue: (plan) => plan.is_custom ? true : (plan.feature_set || []).includes('capability_matrix'),
  },
  {
    label: 'Compliance Matrix',
    icon: Shield,
    gateBy: 'rtm',
    tierValue: (plan) => plan.is_custom ? true : (plan.feature_set || []).includes('rtm'),
  },
  {
    label: 'Proposal Workspace',
    icon: Sparkles,
    gateBy: 'proposal_workspace',
    tierValue: (plan) => plan.is_custom ? true : (plan.feature_set || []).includes('proposal_workspace'),
  },
];

/** Merges a backend BillingPlan with the marketing feature copy above. */
function buildPlanCard(catalogPlan) {
  const visuals = PLAN_VISUALS[catalogPlan.id] || PLAN_VISUALS.GROWTH;
  const releaseRatio = typeof catalogPlan.release_ratio === 'number' ? catalogPlan.release_ratio : 1;
  const releasedCount = (catalogPlan.released_features || []).length;
  const totalCount = releasedCount + (catalogPlan.pending_features || []).length;
  return {
    id: catalogPlan.id,
    name: catalogPlan.name,
    // The backend now sends a discounted display string when the plan's
    // feature set isn't fully released yet. Fall back to the canonical
    // `display_price` for custom plans.
    price: catalogPlan.effective_display || catalogPlan.display_price,
    fullPrice: catalogPlan.display_price,
    discountPct: catalogPlan.discount_pct || 0,
    releasedCount,
    totalCount,
    releaseRatio,
    releasedFeatures: catalogPlan.released_features || [],
    pendingFeatures: catalogPlan.pending_features || [],
    period: catalogPlan.display_period,
    description: catalogPlan.description,
    icon: visuals.icon,
    color: visuals.color,
    features: visuals.features,
    // Pass-through fields the comparison table needs to compute
    // tier values without re-fetching the catalog.
    feature_set: catalogPlan.feature_set || [],
    is_custom: catalogPlan.is_custom,
    limits: {
      max_pursuits: catalogPlan.included_pursuits,
      max_users: catalogPlan.included_seats,
      client_slots: catalogPlan.included_pursuits,
      max_associates: Math.max(0, catalogPlan.included_seats - 1),
    },
    cta: catalogPlan.is_custom ? 'Contact Sales' : 'Start Free Trial',
    popular: catalogPlan.popular,
    base_annual: catalogPlan.base_annual,
    base_monthly: catalogPlan.base_monthly,
  };
}

function PlanCard({ plan, isSelected, onSelect, onCtaClick }) {
  const Icon = plan.icon;
  const colorClasses = {
    slate: {
      border: 'border-slate-200 hover:border-slate-300',
      icon: 'bg-slate-100 text-slate-600',
      badge: 'bg-slate-100 text-slate-700',
      button: 'bg-slate-800 hover:bg-slate-900 text-white',
    },
    teal: {
      border: 'border-teal-200 hover:border-teal-300 ring-2 ring-teal-500',
      icon: 'bg-teal-100 text-teal-600',
      badge: 'bg-teal-100 text-teal-700',
      button: 'bg-teal-700 hover:bg-teal-800 text-white',
    },
    purple: {
      border: 'border-purple-200 hover:border-purple-300',
      icon: 'bg-purple-100 text-purple-600',
      badge: 'bg-purple-100 text-purple-700',
      button: 'bg-purple-700 hover:bg-purple-800 text-white',
    },
  };
  
  const colors = colorClasses[plan.color];

  return (
    <Card 
      className={`relative cursor-pointer transition-all duration-200 ${colors.border} ${isSelected ? 'ring-2 ring-offset-2' : ''}`}
      onClick={() => onSelect(plan.id)}
      data-testid={`plan-card-${plan.id.toLowerCase()}`}
    >
      {plan.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-teal-600 text-white px-3 py-1 text-xs font-semibold">
            <Sparkles className="w-3 h-3 mr-1" />
            Most Popular
          </Badge>
        </div>
      )}
      
      <CardHeader className="text-center pb-4 pt-6">
        <div className={`w-14 h-14 rounded-xl ${colors.icon} flex items-center justify-center mx-auto mb-4`}>
          <Icon className="w-7 h-7" />
        </div>
        <CardTitle className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {plan.name}
        </CardTitle>
        <div className="mt-3">
          <span className="text-base font-semibold text-slate-500 italic">
            Contact for Pricing Info
          </span>
        </div>
        <CardDescription className="mt-2 text-slate-600">
          {plan.description}
        </CardDescription>
      </CardHeader>
      
      <CardContent className="pt-0">
        <ul className="space-y-3 mb-6">
          {plan.features.map((feature, idx) => (
            <li key={idx} className="flex items-center gap-3">
              {feature.included ? (
                <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Check className="w-3 h-3 text-emerald-600" />
                </div>
              ) : (
                <div className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <X className="w-3 h-3 text-slate-400" />
                </div>
              )}
              <span className={`text-sm ${feature.included ? 'text-slate-700' : 'text-slate-400'}`}>
                {feature.text}
                {feature.locked && (
                  <Badge variant="outline" className="ml-2 text-xs border-amber-300 text-amber-600">
                    Pro
                  </Badge>
                )}
              </span>
            </li>
          ))}
        </ul>
        
        <Button 
          className={`w-full ${colors.button}`}
          data-testid={`select-plan-${plan.id.toLowerCase()}`}
          onClick={(e) => {
            // Don't bubble to the Card click handler (which only re-selects).
            // We want a single explicit user action — pick this plan AND
            // open the trial / demo / sales modal.
            e.stopPropagation();
            onSelect(plan.id);
            if (onCtaClick) onCtaClick(plan.id);
          }}
        >
          {plan.cta}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
}

export default function PricingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const dashboardPath = getDashboardPathForUser(user);
  const [searchParams] = useSearchParams();
  const userType = searchParams.get('type') || 'contractor';
  const isConsultant = userType === 'consultant';

  const [contractorPlans, setContractorPlans] = useState([]);
  const [consultantPlans, setConsultantPlans] = useState([]);
  const [foundingCoupon, setFoundingCoupon] = useState(null);
  const [enabledFeatures, setEnabledFeatures] = useState([]);
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(true);
  const [catalogError, setCatalogError] = useState(null);

  const PLANS = isConsultant ? consultantPlans : contractorPlans;
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showTrialModal, setShowTrialModal] = useState(false);

  // Pull live pricing from the backend catalog (single source of truth).
  // No retry logic — the endpoint is public and unauthenticated, so a
  // failure here means the backend is down and a retry won't help.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/billing/plans`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        setContractorPlans((data.contractor_plans || []).map(buildPlanCard));
        setConsultantPlans((data.consultant_plans || []).map(buildPlanCard));
        setFoundingCoupon(data.founding_member_coupon || null);
        setEnabledFeatures(data.enabled_features || []);
        setIsLoadingCatalog(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setCatalogError(e.message);
        setIsLoadingCatalog(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Default-select the popular plan once the catalog arrives, or when
  // the user toggles between contractor and consultant.
  useEffect(() => {
    if (!PLANS.length) return;
    const popular = PLANS.find((p) => p.popular) || PLANS[0];
    setSelectedPlan(popular.id);
  }, [PLANS, isConsultant]);

  const handleSelectPlan = (planId) => {
    setSelectedPlan(planId);
  };

  const handleContinue = () => {
    // Open the Trial Request Modal instead of navigating to registration
    setShowTrialModal(true);
  };

  // Single entry point for any "talk to us" CTA on this page — plan card
  // buttons (Start Free Trial / Contact Sales) and the bottom Schedule a
  // Demo button all funnel through this so the modal is the canonical
  // place we collect lead info, regardless of which button triggered it.
  const handleOpenRequestModal = (planId) => {
    if (planId) setSelectedPlan(planId);
    setShowTrialModal(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="text-sm">Back to home</span>
            </button>
            
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">F</span>
              </div>
              <span className="font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                FedBD
              </span>
            </div>
            
            {isAuthenticated && user ? (
              <Button
                className="bg-teal-600 hover:bg-teal-500 text-white"
                onClick={() => navigate(dashboardPath)}
                data-testid="pricing-go-to-dashboard-btn"
              >
                Go to Dashboard
                <ArrowRightCircle className="w-4 h-4 ml-1.5" />
              </Button>
            ) : (
              <Button
                variant="outline"
                className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white"
                onClick={() => navigate('/login')}
                data-testid="pricing-sign-in-btn"
              >
                Sign in
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-slate-900 text-white pb-20 pt-12">
        <div className="max-w-4xl mx-auto text-center px-4">
          <Badge className={`${isConsultant ? 'bg-purple-500/20 text-purple-300 border-purple-500/30' : 'bg-teal-500/20 text-teal-300 border-teal-500/30'} mb-4`}>
            {isConsultant ? <Briefcase className="w-3 h-3 mr-1" /> : <Radar className="w-3 h-3 mr-1" />}
            {isConsultant ? 'Consultant Plans' : 'Contractor Plans'}
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {isConsultant ? 'Grow Your Consulting Practice' : 'Find, Analyze & Win'}
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            {isConsultant 
              ? 'Manage multiple contractor clients from one unified dashboard. Help them discover opportunities and win more contracts.'
              : 'From SAM.gov opportunity discovery to AI-powered bid analysis. Select the plan that fits your capture management needs.'
            }
          </p>
          
          {/* Type Switcher */}
          <div className="mt-6 flex justify-center gap-2">
            <Button
              variant={!isConsultant ? 'default' : 'outline'}
              size="sm"
              onClick={() => navigate('/pricing?type=contractor')}
              className={!isConsultant ? 'bg-teal-600 hover:bg-teal-700' : 'border-slate-600 text-slate-300 hover:bg-slate-800'}
            >
              <Building2 className="w-4 h-4 mr-2" />
              Contractor
            </Button>
            <Button
              variant={isConsultant ? 'default' : 'outline'}
              size="sm"
              onClick={() => navigate('/pricing?type=consultant')}
              className={isConsultant ? 'bg-purple-600 hover:bg-purple-700' : 'border-slate-600 text-slate-300 hover:bg-slate-800'}
            >
              <Briefcase className="w-4 h-4 mr-2" />
              Consultant
            </Button>
          </div>
        </div>
      </div>

      {/* Founding Member promotion banner — pricing masked */}

      {/* Pricing Cards */}
      <div className="max-w-6xl mx-auto px-4 -mt-2">
        {isLoadingCatalog ? (
          <div className="flex flex-col items-center justify-center py-20"
               data-testid="pricing-loading">
            <Loader2 className="w-8 h-8 text-teal-600 animate-spin mb-3" />
            <p className="text-sm text-slate-500">Loading live pricing...</p>
          </div>
        ) : catalogError ? (
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-6 text-center"
               data-testid="pricing-error">
            <p className="text-sm text-rose-800">
              Pricing temporarily unavailable. Please refresh or contact{' '}
              <a href="mailto:sales@fedbd.ai" className="underline">sales@fedbd.ai</a>.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PLANS.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                isSelected={selectedPlan === plan.id}
                onSelect={handleSelectPlan}
                onCtaClick={handleOpenRequestModal}
              />
            ))}
          </div>
        )}
      </div>

      {/* Continue Button */}
      <div className="max-w-6xl mx-auto px-4 mt-10 text-center">
        <Button
          size="lg"
          className="bg-teal-700 hover:bg-teal-800 text-white px-8"
          onClick={handleContinue}
          data-testid="continue-to-register-btn"
        >
          Continue with {PLANS.find(p => p.id === selectedPlan)?.name}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
        <p className="text-sm text-slate-500 mt-3">
          Review within 24 hours • Contact us for pricing details
        </p>
      </div>

      {/* Feature Comparison */}
      <div className="max-w-6xl mx-auto px-4 mt-20 mb-20">
        <h2 className="text-2xl font-bold text-slate-900 text-center mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Feature Comparison
        </h2>
        <p className="text-center text-sm text-slate-500 mb-8">
          Showing the {isConsultant ? 'Consultant' : 'Contractor'} tier comparison.
          Items marked <span className="font-semibold text-amber-700">Coming Soon</span> are
          included in the plan and will activate the moment we ship them — no upgrade required.
        </p>

        {PLANS.length === 3 && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full" data-testid="pricing-comparison-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left py-4 px-6 font-semibold text-slate-700">Feature</th>
                  {PLANS.map((plan) => (
                    <th
                      key={plan.id}
                      className={`text-center py-4 px-6 font-semibold ${plan.popular ? 'text-teal-700 bg-teal-50' : 'text-slate-700'}`}
                      data-testid={`pricing-comparison-tier-${plan.id.toLowerCase()}`}
                    >
                      {plan.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {COMPARISON_ROWS.map((row) => {
                  const RowIcon = row.icon;
                  const releasedGlobally = !row.gateBy || enabledFeatures.includes(row.gateBy);
                  return (
                    <tr key={row.label} data-testid={`pricing-comparison-row-${row.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                      <td className="py-4 px-6 text-slate-700">
                        <div className="flex items-center gap-2">
                          <RowIcon className="w-4 h-4 text-slate-400" />
                          <span>{row.label}</span>
                          {!releasedGlobally && (
                            <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] px-2 py-0">
                              Coming Soon
                            </Badge>
                          )}
                        </div>
                      </td>
                      {PLANS.map((plan) => {
                        const value = row.tierValue(plan);
                        const cellBg = plan.popular ? 'bg-teal-50/50' : '';
                        const cellTextEmphasis = plan.popular ? 'text-teal-700 font-medium' : 'text-slate-600';
                        if (value === false) {
                          return (
                            <td key={plan.id} className={`py-4 px-6 text-center ${cellBg}`}>
                              <X className="w-5 h-5 text-slate-300 mx-auto" />
                            </td>
                          );
                        }
                        if (value === true) {
                          const checkColor = plan.popular ? 'text-teal-600' : plan.is_custom ? 'text-emerald-600' : 'text-slate-600';
                          return (
                            <td key={plan.id} className={`py-4 px-6 text-center ${cellBg}`}>
                              <Check className={`w-5 h-5 ${checkColor} mx-auto`} />
                            </td>
                          );
                        }
                        return (
                          <td key={plan.id} className={`py-4 px-6 text-center ${cellTextEmphasis} ${cellBg}`}>
                            {value}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* AI Credits — explicit callout that they're prepaid + sold separately */}
      <div className="max-w-6xl mx-auto px-4 mb-20" data-testid="pricing-ai-credits-callout">
        <Card className="border-teal-200 bg-gradient-to-br from-teal-50 to-emerald-50">
          <CardHeader>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <CardTitle className="text-xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Brain className="w-5 h-5 text-teal-600" />
                  AI Credits — pay only for what you use
                </CardTitle>
                <CardDescription className="mt-2 text-slate-700 max-w-3xl">
                  Plans <span className="font-semibold">do not include AI usage</span>.
                  Buy a prepaid pack and use it across the entire workspace —
                  every team member draws from the same balance. Bring your own
                  model key (BYOM) and your credits stretch significantly further.
                  Trial accounts ship with <span className="font-semibold">$25 in starter credits</span>.
                </CardDescription>
              </div>
              <Badge className="bg-teal-100 text-teal-800 border-teal-200">Prepaid · pay-as-you-go</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white border border-slate-200 rounded-lg p-4 text-center">
                <div className="text-sm font-semibold text-slate-500 italic">Contact for Pricing</div>
                <div className="text-xs text-slate-500 mt-1">Starter pack</div>
                <div className="text-[11px] text-slate-400 mt-2">~1 month for a single-pursuit team</div>
              </div>
              <div className="bg-white border-2 border-teal-300 rounded-lg p-4 text-center relative">
                <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-teal-600 text-white text-[10px]">Most Popular</Badge>
                <div className="text-sm font-semibold text-slate-500 italic">Contact for Pricing</div>
                <div className="text-xs text-slate-500 mt-1">Growth pack</div>
                <div className="text-[11px] text-slate-400 mt-2">2-3 months at typical Growth-tier usage</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-4 text-center">
                <div className="text-sm font-semibold text-slate-500 italic">Contact for Pricing</div>
                <div className="text-xs text-slate-500 mt-1">Power pack</div>
                <div className="text-[11px] text-slate-400 mt-2">High-velocity teams running parallel pursuits</div>
              </div>
            </div>
            <div className="mt-4 text-xs text-slate-500 flex items-start gap-2">
              <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>
                When credits hit zero, AI features pause after a 24-hour grace window.
                Optional auto-recharge can refill automatically (configurable in Settings → AI Configuration).
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* FAQ or Support */}
      <div className="bg-slate-50 py-16">
        <div className="max-w-3xl mx-auto text-center px-4">
          <h3 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Not sure which plan is right for you?
          </h3>
          <p className="text-slate-600 mb-6">
            Our team can help you choose the perfect plan based on your capture pipeline and team size.
          </p>
          <Button
            variant="outline"
            className="border-slate-300"
            onClick={() => handleOpenRequestModal()}
            data-testid="schedule-demo-btn"
          >
            Schedule a Demo
          </Button>
        </div>
      </div>

      {/* Trial Request Modal */}
      <TrialRequestModal 
        isOpen={showTrialModal} 
        onClose={() => setShowTrialModal(false)}
        selectedPlan={selectedPlan}
        requestType={isConsultant ? 'CONSULTANCY' : 'CONTRACTOR'}
      />
    </div>
  );
}
